import React from "react";
import { Link } from "react-router-dom";
import { Award, Leaf, Hammer, Heart } from "lucide-react";
import Layout from "@/components/Layout";
import { ScrollFade, StaggerGroup, StaggerItem, HeroStaggerText, Counter } from "@/lib/animations";

const VALUES = [
  { icon: Award,  title: "Quality first", body: "Every jacket is inspected by hand before it leaves our studio. If we wouldn't wear it ourselves, we don't ship it." },
  { icon: Leaf,   title: "Cruelty-free luxury", body: "Our vegan PU leathers and faux furs match the hand and depth of the real thing — without the cost to animals." },
  { icon: Hammer, title: "Made in India", body: "Cut, stitched and finished by skilled tailors across Delhi and Ludhiana. We pay fairly and we know their names." },
  { icon: Heart,  title: "Built to last", body: "Reinforced seams, premium hardware, two-year stitching warranty. A jacket should outlive a trend." },
];

export default function About() {
  return (
    <Layout>
      <section className="bg-[#163A2F] text-[#F8F6F0] grain relative pt-20 pb-24 md:pt-28 md:pb-32 overflow-hidden">
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="overline text-[#C9A227]">Our story</div>
          <HeroStaggerText
            text="Jackets, with intent."
            className="mt-3 text-5xl md:text-7xl font-extralight tracking-tight max-w-4xl"
          />
          <p className="mt-6 max-w-xl text-[#F8F6F0]/75 font-light text-base md:text-lg leading-relaxed">
            Norbu Collection was born from a simple question — why does great menswear in India have to come from abroad? We exist to answer it, one considered jacket at a time.
          </p>
        </div>
      </section>

      {/* Story split */}
      <section className="bg-[#F8F6F0] py-20 md:py-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <ScrollFade className="relative aspect-[4/5] overflow-hidden">
            <img
              src="https://images.pexels.com/photos/6626749/pexels-photo-6626749.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Norbu studio"
              className="w-full h-full object-cover"
            />
          </ScrollFade>
          <ScrollFade delay={0.1}>
            <div className="overline text-[#C9A227]">The beginning</div>
            <h2 className="mt-3 text-3xl md:text-4xl font-light text-[#163A2F] tracking-tight">
              From a workshop floor<br />
              <span className="italic text-[#C9A227] font-extralight">to your wardrobe.</span>
            </h2>
            <div className="mt-6 space-y-4 text-[#163A2F]/80 font-light leading-relaxed">
              <p>
                What began as a B2B jacket house supplying brands across India has grown into a direct-to-customer label. We learned how the best jackets are made — and we wanted to bring that same craft straight to the people who wear them.
              </p>
              <p>
                No middlemen. No mark-ups. Just refined silhouettes, premium fabrications and a fair price.
              </p>
            </div>
          </ScrollFade>
        </div>
      </section>

      {/* Inside the Studio — real workshop photos */}
      <section className="bg-[#0A1C16] text-[#F8F6F0] grain relative py-20 md:py-28 overflow-hidden" data-testid="studio-gallery-section">
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
          <ScrollFade>
            <div className="overline text-[#C9A227]">Inside the studio</div>
            <h2 className="mt-3 text-3xl md:text-5xl font-light tracking-tight max-w-3xl">
              Where every jacket <span className="italic text-[#C9A227]">takes shape.</span>
            </h2>
            <p className="mt-5 max-w-xl text-[#F8F6F0]/70 font-light leading-relaxed">
              Six hands, one jacket. From the cutting table to the final press, this is our Delhi workshop — unfiltered.
            </p>
          </ScrollFade>

          <StaggerGroup className="mt-14 grid grid-cols-12 gap-3 md:gap-5">
            {/* Tall left */}
            <StaggerItem className="col-span-6 md:col-span-5 row-span-2">
              <figure className="relative overflow-hidden aspect-[3/4] group">
                <img src="/studio/cutting-table.jpg" alt="Pattern cutting on the studio floor" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-5">
                  <div className="overline text-[#C9A227]">01</div>
                  <div className="text-sm md:text-base font-light mt-1">Pattern &amp; cut</div>
                </figcaption>
              </figure>
            </StaggerItem>

            {/* Top right pair */}
            <StaggerItem className="col-span-6 md:col-span-4">
              <figure className="relative overflow-hidden aspect-[4/3] group">
                <img src="/studio/sewing-pair.jpg" alt="Tailors at work" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-5">
                  <div className="overline text-[#C9A227]">02</div>
                  <div className="text-sm md:text-base font-light mt-1">Hand-finish</div>
                </figcaption>
              </figure>
            </StaggerItem>

            <StaggerItem className="hidden md:block md:col-span-3">
              <figure className="relative overflow-hidden aspect-[4/3] group h-full">
                <img src="/studio/fabric-storage.jpg" alt="Premium fabric storage" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-5">
                  <div className="overline text-[#C9A227]">03</div>
                  <div className="text-sm md:text-base font-light mt-1">Fabric vault</div>
                </figcaption>
              </figure>
            </StaggerItem>

            {/* Bottom right wide */}
            <StaggerItem className="col-span-6 md:col-span-7">
              <figure className="relative overflow-hidden aspect-[16/9] group">
                <img src="/studio/sewing-wide.jpg" alt="Sewing floor at Norbu Collection" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-5">
                  <div className="overline text-[#C9A227]">04</div>
                  <div className="text-sm md:text-base font-light mt-1">The sewing floor</div>
                </figcaption>
              </figure>
            </StaggerItem>

            <StaggerItem className="col-span-6 md:col-span-6">
              <figure className="relative overflow-hidden aspect-[4/3] group">
                <img src="/studio/sewing-floor.jpg" alt="Rows of tailors stitching" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-5">
                  <div className="overline text-[#C9A227]">05</div>
                  <div className="text-sm md:text-base font-light mt-1">Stitch &amp; assemble</div>
                </figcaption>
              </figure>
            </StaggerItem>

            <StaggerItem className="col-span-6">
              <figure className="relative overflow-hidden aspect-[4/3] group">
                <img src="/studio/finished-goods.jpg" alt="Finished jackets ready to ship" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-5">
                  <div className="overline text-[#C9A227]">06</div>
                  <div className="text-sm md:text-base font-light mt-1">Ready to ship</div>
                </figcaption>
              </figure>
            </StaggerItem>
          </StaggerGroup>

          <ScrollFade>
            <div className="mt-12 flex items-center gap-3 text-[#F8F6F0]/60 font-light text-sm">
              <span className="h-px w-10 bg-[#C9A227]/60" />
              <span>Delhi &amp; Ludhiana · India</span>
            </div>
          </ScrollFade>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#F8F6F0] py-20 md:py-24 border-t hairline">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
          <ScrollFade>
            <div className="overline text-[#C9A227]">What we stand for</div>
            <h2 className="mt-3 text-3xl md:text-5xl font-light text-[#163A2F] tracking-tight">Our principles</h2>
          </ScrollFade>
          <StaggerGroup className="mt-12 grid sm:grid-cols-2 gap-8 md:gap-12">
            {VALUES.map(({ icon: Icon, title, body }, i) => (
              <StaggerItem key={i}>
                <div className="flex gap-5">
                  <Icon className="w-7 h-7 text-[#C9A227] flex-shrink-0 mt-1" strokeWidth={1.4} />
                  <div>
                    <h3 className="text-xl font-medium text-[#163A2F]">{title}</h3>
                    <p className="mt-2 text-[#163A2F]/75 font-light leading-relaxed">{body}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#163A2F] text-[#F8F6F0] grain relative py-20 md:py-24">
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
          <ScrollFade>
            <div className="overline text-[#C9A227]">Our promise</div>
            <h2 className="mt-3 text-3xl md:text-5xl font-light tracking-tight max-w-3xl">
              Quietly confident, <span className="italic text-[#C9A227]">in writing.</span>
            </h2>
          </ScrollFade>
          <StaggerGroup className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-10">
            {[
              { n: 9,   label: "Premium fabrications",    suffix: "" },
              { n: 100, label: "Made in India",           suffix: "%" },
              { n: 24,  label: "Hour reply guarantee",    suffix: "h" },
              { n: 2,   label: "Year stitching warranty", suffix: "yr" },
            ].map((s, i) => (
              <StaggerItem key={i}>
                <div className="text-5xl md:text-6xl font-extralight text-[#C9A227] tracking-tight">
                  <Counter to={s.n} suffix={s.suffix} />
                </div>
                <div className="mt-3 overline text-[#F8F6F0]/70">{s.label}</div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#F8F6F0] py-20 md:py-24 text-center">
        <ScrollFade>
          <div className="overline text-[#C9A227]">Find your jacket</div>
          <h2 className="mt-3 text-3xl md:text-5xl font-light text-[#163A2F] tracking-tight">
            Crafted for you. <span className="italic text-[#C9A227]">Worn forever.</span>
          </h2>
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <Link to="/categories" className="bg-[#163A2F] text-white px-8 py-4 uppercase text-xs tracking-[0.25em]">Browse categories</Link>
            <Link to="/contact" className="border border-[#163A2F] text-[#163A2F] px-8 py-4 uppercase text-xs tracking-[0.25em]">Talk to us</Link>
          </div>
        </ScrollFade>
      </section>
    </Layout>
  );
}
